/*
Small date/time helpers on dayjs, replacing the prototype-mutating `datejs`
(which globally overrode `Date.parse` to return a Date and added
`Date.prototype.toString(format)`) and the one-off `moment` time parse.

Centralizing here keeps every date operation on a single, non-global library,
and makes the loose-time-string parsing testable in one place.
*/
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

// The shapes a loosely-typed time value arrives in: user input, stored values
// ("02:30 pm"), and the "12:00am" / "11:59pm" defaults. Tried in order, strictly.
const TIME_FORMATS = [
  "h:mm a",
  "h:mm A",
  "hh:mm a",
  "hh:mm A",
  "h:mma",
  "h:mmA",
  "hh:mma",
  "hh:mmA",
  "H:mm",
  "HH:mm",
  "h a",
  "h A",
  "ha",
  "hA",
];

function parseTime(value) {
  if (value == null || value === "") return null;
  const parsed = dayjs(String(value).trim(), TIME_FORMATS, true);
  return parsed.isValid() ? parsed : null;
}

// "2:30 PM" — display / validation canonical form (was datejs `.toString('h:mm tt')`).
export function normalizeTime(value) {
  const parsed = parseTime(value);
  return parsed ? parsed.format("h:mm A") : null;
}

// "02:30 pm" — storage canonical form (was `moment(v, 'hh:mm a').format('hh:mm a')`).
export function normalizeTimeInput(value) {
  const parsed = parseTime(value);
  return parsed ? parsed.format("hh:mm a") : null;
}

// A Date -> "M/D/YYYY"; anything else is returned unchanged. Mirrors datejs's
// `.toString('M/d/yyyy')`, where calling it on a plain string ignored the
// format argument and returned the string as-is.
export function formatDateMDY(value) {
  return value instanceof Date ? dayjs(value).format("M/D/YYYY") : value;
}
